import { useCallback, useEffect, useState } from "react";
import {
  type AdminBroadcastRecord,
  fetchAdminBroadcasts,
} from "../../api/admin-communications";
import { PageHeader } from "../../components/PageHeader";
import {
  ErrorBanner,
  LoadingIndicator,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { CommunicationsCompose } from "./CommunicationsCompose";
import { CommunicationsHistory } from "./CommunicationsHistory";

type CommunicationsTab = "compose" | "history";

const TABS: ReadonlyArray<CommunicationsTab> = ["compose", "history"];

export function CommunicationsPage(): React.JSX.Element {
  const { broadcasts, error, loading, refresh } = useBroadcastHistory();
  const [activeTab, setActiveTab] = useState<CommunicationsTab>("compose");

  return (
    <div>
      <PageHeader
        title={t("communications.title")}
        description={t("communications.description")}
        actions={
          <button type="button" onClick={refresh} style={shared.refreshBtn}>
            {t("common.refresh")}
          </button>
        }
      />
      <TabBar active={activeTab} onChange={setActiveTab} />
      {error && <ErrorBanner message={error} />}
      {activeTab === "compose" && (
        <CommunicationsCompose
          onCreated={() => {
            setActiveTab("history");
            void refresh();
          }}
        />
      )}
      {activeTab === "history" && (
        <HistoryPanel broadcasts={broadcasts} loading={loading} />
      )}
    </div>
  );
}

function useBroadcastHistory(): {
  readonly broadcasts: ReadonlyArray<AdminBroadcastRecord>;
  readonly error: string | null;
  readonly loading: boolean;
  readonly refresh: () => Promise<void>;
} {
  const [broadcasts, setBroadcasts] = useState<
    ReadonlyArray<AdminBroadcastRecord>
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setBroadcasts(await fetchAdminBroadcasts(20));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { broadcasts, error, loading, refresh };
}

function TabBar({
  active,
  onChange,
}: {
  active: CommunicationsTab;
  onChange: (tab: CommunicationsTab) => void;
}): React.JSX.Element {
  return (
    <div style={styles.tabBar}>
      {TABS.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(tab)}
          style={{
            ...ts.filterBtn,
            ...(active === tab ? ts.filterBtnActive : {}),
          }}
        >
          {t(`communications.tab_${tab}`)}
        </button>
      ))}
    </div>
  );
}

function HistoryPanel({
  broadcasts,
  loading,
}: {
  broadcasts: ReadonlyArray<AdminBroadcastRecord>;
  loading: boolean;
}): React.JSX.Element {
  if (loading) return <LoadingIndicator />;
  return <CommunicationsHistory broadcasts={broadcasts} />;
}

const styles: Record<string, React.CSSProperties> = {
  tabBar: { display: "flex", gap: 8, marginBottom: 16 },
};
